import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shapes } from './Shapes';
import { Check, RefreshCw, Star, Info, MousePointer2, Box } from 'lucide-react';
import confetti from 'canvas-confetti';
import Quiz from './Quiz';
import { useAuth } from '../../context/AuthContext';

import { sounds } from '../../utils/SoundManager';

const Section = ({ title, children, className, id }) => (
    <section id={id} className={`bg-white rounded-3xl p-6 shadow-xl border-4 border-brand-blue/10 ${className}`}>
        <h2 className="text-2xl md:text-3xl font-bold text-brand-purple mb-6 font-hand">{title}</h2>
        {children}
    </section>
);

const ShapeNets = () => {
    const [activeShape, setActiveShape] = useState('cube');
    const [isUnfolded, setIsUnfolded] = useState(false);

    const shapes = [
        { id: 'cube', name: 'Cubo', color: 'bg-brand-green', stroke: '#4CC9F0' },
        { id: 'cylinder', name: 'Cilindro', color: 'bg-brand-pink', stroke: '#F72585' },
        { id: 'pyramid', name: 'Pirámide', color: 'bg-brand-purple', stroke: '#7209B7' }
    ];

    const getNetAnimation = () => {
        const strokeColor = shapes.find(s => s.id === activeShape)?.stroke;
        const fillColor = `${strokeColor}20`; // 20% opacity hex
        
        switch (activeShape) {
            case 'cube':
                return (
                    <div className="relative w-64 h-64 flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            {!isUnfolded ? (
                                <motion.div 
                                    key="3d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="w-32 h-32"
                                >
                                    <Shapes.CubePrism stroke={strokeColor} fill={fillColor} />
                                </motion.div>
                            ) : (
                                <motion.div 
                                    key="2d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="relative w-48 h-64 grid grid-cols-3 grid-rows-4 gap-1 p-2 bg-slate-50 rounded-xl"
                                >
                                    {/* Cross shape for cube net */}
                                    <div />
                                    <motion.div initial={{ rotateX: -90, transformOrigin: 'bottom' }} animate={{ rotateX: 0 }} className="bg-brand-green/30 border-2 border-brand-green rounded-sm" />
                                    <div />
                                    
                                    <motion.div initial={{ rotateY: 90, transformOrigin: 'right' }} animate={{ rotateY: 0 }} className="bg-brand-green/30 border-2 border-brand-green rounded-sm" />
                                    <div className="bg-brand-green/50 border-2 border-brand-green rounded-sm flex items-center justify-center"><span className="text-brand-green font-bold text-xs">Base</span></div>
                                    <motion.div initial={{ rotateY: -90, transformOrigin: 'left' }} animate={{ rotateY: 0 }} className="bg-brand-green/30 border-2 border-brand-green rounded-sm" />
                                    
                                    <div />
                                    <motion.div initial={{ rotateX: 90, transformOrigin: 'top' }} animate={{ rotateX: 0 }} className="bg-brand-green/30 border-2 border-brand-green rounded-sm" />
                                    <div />
                                    
                                    <div />
                                    <motion.div initial={{ rotateX: 90, transformOrigin: 'top' }} animate={{ rotateX: 0 }} transition={{ delay: 0.1 }} className="bg-brand-green/30 border-2 border-brand-green rounded-sm" />
                                    <div />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            case 'cylinder':
                return (
                    <div className="relative w-64 h-64 flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            {!isUnfolded ? (
                                <motion.div 
                                    key="3d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="w-32 h-32"
                                >
                                    <Shapes.Cylinder stroke={strokeColor} fill={fillColor} />
                                </motion.div>
                            ) : (
                                <motion.div 
                                    key="2d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="relative w-full h-full flex flex-col items-center justify-center gap-2 p-4"
                                >
                                    <motion.div initial={{ y: 20, rotateX: -60 }} animate={{ y: 0, rotateX: 0 }} className="w-16 h-16 rounded-full bg-brand-pink/30 border-2 border-brand-pink shrink-0" />
                                    <motion.div initial={{ scaleX: 0.2 }} animate={{ scaleX: 1 }} className="w-48 h-24 bg-brand-pink/30 border-2 border-brand-pink rounded-sm" />
                                    <motion.div initial={{ y: -20, rotateX: 60 }} animate={{ y: 0, rotateX: 0 }} className="w-16 h-16 rounded-full bg-brand-pink/30 border-2 border-brand-pink shrink-0" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            case 'pyramid':
                return (
                    <div className="relative w-64 h-64 flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            {!isUnfolded ? (
                                <motion.div 
                                    key="3d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="w-32 h-32"
                                >
                                    <Shapes.SquarePyramid stroke={strokeColor} fill={fillColor} />
                                </motion.div>
                            ) : (
                                <motion.div 
                                    key="2d"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    className="relative w-48 h-48 flex items-center justify-center"
                                >
                                    {/* Square Base */}
                                    <div className="w-16 h-16 bg-brand-purple/50 border-2 border-brand-purple absolute flex items-center justify-center z-10"><span className="text-white font-bold text-xs">Base</span></div>
                                    
                                    {/* 4 Triangles */}
                                    <motion.div initial={{ rotateX: -120, transformOrigin: 'bottom' }} animate={{ rotateX: 0 }} className="w-0 h-0 border-l-[32px] border-r-[32px] border-b-[48px] border-l-transparent border-r-transparent border-b-brand-purple/30 absolute -top-[48px] drop-shadow-[0_2px_0_rgba(114,9,183,1)]" />
                                    <motion.div initial={{ rotateX: 120, transformOrigin: 'top' }} animate={{ rotateX: 0 }} className="w-0 h-0 border-l-[32px] border-r-[32px] border-t-[48px] border-l-transparent border-r-transparent border-t-brand-purple/30 absolute -bottom-[48px] drop-shadow-[0_-2px_0_rgba(114,9,183,1)]" />
                                    <motion.div initial={{ rotateY: 120, transformOrigin: 'right' }} animate={{ rotateY: 0 }} className="w-0 h-0 border-t-[32px] border-b-[32px] border-r-[48px] border-t-transparent border-b-transparent border-r-brand-purple/30 absolute -left-[48px] drop-shadow-[2px_0_0_rgba(114,9,183,1)]" />
                                    <motion.div initial={{ rotateY: -120, transformOrigin: 'left' }} animate={{ rotateY: 0 }} className="w-0 h-0 border-t-[32px] border-b-[32px] border-l-[48px] border-t-transparent border-b-transparent border-l-brand-purple/30 absolute -right-[48px] drop-shadow-[-2px_0_0_rgba(114,9,183,1)]" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="bg-slate-50 p-6 rounded-3xl border-2 border-slate-200">
            <p className="text-gray-600 mb-6 text-center">¿Cómo se vería un cuerpo geométrico si lo abrimos y lo estiramos sobre la mesa? A esto se le llama <strong>Desarrollo Plano</strong>.</p>
            
            <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Shape Selector */}
                <div className="flex justify-center md:flex-col gap-3">
                    {shapes.map(shape => (
                        <button
                            key={shape.id}
                            onClick={() => {
                                sounds.playPop();
                                setActiveShape(shape.id);
                                setIsUnfolded(false);
                            }}
                            className={`px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${
                                activeShape === shape.id 
                                ? `${shape.color} text-white shadow-md scale-105` 
                                : 'bg-white text-gray-500 border-2 border-gray-100 hover:border-gray-300'
                            }`}
                        >
                            {shape.name}
                        </button>
                    ))}
                </div>

                {/* Animation Area */}
                <div className="bg-white p-8 rounded-3xl shadow-inner border-2 border-gray-100 flex flex-col items-center">
                    {getNetAnimation()}
                    
                    <button
                        onClick={() => {
                            sounds.playAction();
                            setIsUnfolded(!isUnfolded);
                        }}
                        className={`mt-6 px-8 py-3 rounded-full font-bold text-white transition-colors flex items-center gap-2 ${
                            isUnfolded ? 'bg-brand-orange hover:bg-orange-500' : 'bg-brand-blue hover:bg-blue-600'
                        }`}
                    >
                        {isUnfolded ? '📦 Armar Figura (3D)' : '✂️ Desplegar Plano (2D)'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PrismExplorer = () => {
    const [sides, setSides] = useState(4);

    const getPrismShape = () => {
        switch (sides) {
            case 3: return <Shapes.TriangularPrism stroke="#4CC9F0" fill="#4CC9F020" />;
            case 4: return <Shapes.CubePrism stroke="#4CC9F0" fill="#4CC9F020" />; // Square prism
            case 5: return <Shapes.PentagonalPrism stroke="#4CC9F0" fill="#4CC9F020" />;
            case 6: return <Shapes.HexagonalPrism stroke="#4CC9F0" fill="#4CC9F020" />;
            default: return <Shapes.CubePrism stroke="#4CC9F0" fill="#4CC9F020" />;
        }
    };

    const getBaseName = () => {
        switch (sides) {
            case 3: return "Base Triangular (3 lados)";
            case 4: return "Base Cuadrada (4 lados)";
            case 5: return "Base Pentagonal (5 lados)";
            case 6: return "Base Hexagonal (6 lados)";
            default: return "Base Poligonal";
        }
    };

    return (
        <div className="bg-blue-50 p-6 rounded-3xl border-2 border-blue-100 flex flex-col items-center gap-6">
            <h3 className="text-xl font-bold text-brand-blue font-hand text-center">Explorador de Prismas</h3>
            <p className="text-gray-600 text-center text-sm mb-2">Desliza para cambiar la base del prisma</p>
            
            <div className="w-48 h-48 relative">
                <AnimatePresence mode="popLayout">
                    <motion.div
                        key={sides}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.2 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0"
                    >
                        {getPrismShape()}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="w-full max-w-xs space-y-4">
                <div className="flex justify-between text-brand-blue font-bold">
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                    <span>6</span>
                </div>
                <input 
                    type="range" 
                    min="3" 
                    max="6" 
                    value={sides} 
                    onChange={(e) => {
                        sounds.playAction();
                        setSides(parseInt(e.target.value));
                    }}
                    className="w-full h-3 bg-white rounded-lg appearance-none cursor-pointer border-2 border-blue-200 accent-brand-blue"
                />
                <p className="text-center font-bold text-brand-blue bg-white py-2 rounded-xl border border-blue-100 shadow-sm">
                    {getBaseName()}
                </p>
            </div>
        </div>
    );
};

const PyramidExplorer = () => {
    const [sides, setSides] = useState(3);

    const getPyramidShape = () => {
        switch (sides) {
            case 3: return <Shapes.TriangularPyramid stroke="#7209B7" fill="#7209B720" />;
            case 4: return <Shapes.SquarePyramid stroke="#7209B7" fill="#7209B720" />;
            case 5: return <Shapes.PentagonalPyramid stroke="#7209B7" fill="#7209B720" />;
            case 6: return <Shapes.HexagonalPyramid stroke="#7209B7" fill="#7209B720" />;
            default: return <Shapes.Pyramid stroke="#7209B7" fill="#7209B720" />;
        }
    };

    const getBaseName = () => {
        switch (sides) {
            case 3: return "Base Triangular (3 lados)";
            case 4: return "Base Cuadrada (4 lados)";
            case 5: return "Base Pentagonal (5 lados)";
            case 6: return "Base Hexagonal (6 lados)";
            default: return "Base Poligonal";
        }
    };

    return (
        <div className="bg-purple-50 p-6 rounded-3xl border-2 border-purple-100 flex flex-col items-center gap-6">
            <h3 className="text-xl font-bold text-brand-purple font-hand text-center">Explorador de Pirámides</h3>
            <p className="text-gray-600 text-center text-sm mb-2">Desliza para cambiar la base de la pirámide</p>
            
            <div className="w-48 h-48 relative">
                <AnimatePresence mode="popLayout">
                    <motion.div
                        key={sides}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.2 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0"
                    >
                        {getPyramidShape()}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="w-full max-w-xs space-y-4">
                <div className="flex justify-between text-brand-purple font-bold">
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                    <span>6</span>
                </div>
                <input 
                    type="range" 
                    min="3" 
                    max="6" 
                    value={sides} 
                    onChange={(e) => {
                        sounds.playAction();
                        setSides(parseInt(e.target.value));
                    }}
                    className="w-full h-3 bg-white rounded-lg appearance-none cursor-pointer border-2 border-purple-200 accent-brand-purple"
                />
                <p className="text-center font-bold text-brand-purple bg-white py-2 rounded-xl border border-purple-100 shadow-sm">
                    {getBaseName()}
                </p>
            </div>
        </div>
    );
};

const ShapeMatchingGame3D = () => {
    const [selectedShape, setSelectedShape] = useState(null);
    const [selectedName, setSelectedName] = useState(null);
    const [matches, setMatches] = useState([]); // Array of ids
    const [wrongMatch, setWrongMatch] = useState(null); // {shapeId, nameId}

    const data = [
        { id: 'sph', name: 'Esfera', Icon: Shapes.Sphere, color: 'text-brand-blue' },
        { id: 'cyl', name: 'Cilindro', Icon: Shapes.Cylinder, color: 'text-brand-pink' },
        { id: 'con', name: 'Cono', Icon: Shapes.Cone, color: 'text-brand-orange' },
        { id: 'pri', name: 'Prisma', Icon: Shapes.CubePrism, color: 'text-brand-green' },
        { id: 'pyr', name: 'Pirámide', Icon: Shapes.Pyramid, color: 'text-brand-purple' },
    ];

    const [shuffledNames] = useState(() => [...data].sort(() => Math.random() - 0.5));

    const handleShapeClick = (id) => {
        if (matches.includes(id)) return;
        sounds.playPop();
        setSelectedShape(id);
        if (selectedName) {
            checkMatch(id, selectedName);
        }
    };

    const handleNameClick = (id) => {
        if (matches.includes(id)) return;
        sounds.playPop();
        setSelectedName(id);
        if (selectedShape) {
            checkMatch(selectedShape, id);
        }
    };

    const checkMatch = (sId, nId) => {
        if (sId === nId) {
            setMatches(prev => [...prev, sId]);
            sounds.playCorrect();
            setSelectedShape(null);
            setSelectedName(null);
            if (matches.length + 1 === data.length) {
                confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            }
        } else {
            setWrongMatch({ sId, nId });
            sounds.playIncorrect();
            setTimeout(() => {
                setWrongMatch(null);
                setSelectedShape(null);
                setSelectedName(null);
            }, 600);
        }
    };

    const reset = () => {
        sounds.playAction();
        setMatches([]);
        setSelectedShape(null);
        setSelectedName(null);
    };

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-2 gap-12 max-w-2xl mx-auto items-start">
                <div className="space-y-4">
                    <h4 className="text-center font-bold text-gray-400 uppercase text-xs tracking-widest mb-4">Figura 3D</h4>
                    {data.map((item) => (
                        <motion.button
                            key={item.id}
                            whileHover={{ scale: matches.includes(item.id) ? 1 : 1.05 }}
                            whileTap={{ scale: matches.includes(item.id) ? 1 : 0.95 }}
                            onClick={() => handleShapeClick(item.id)}
                            className={`
                                w-full h-24 p-4 rounded-2xl border-4 transition-all flex items-center justify-center
                                ${matches.includes(item.id) ? 'bg-green-50 border-green-500 opacity-50' :
                                    selectedShape === item.id ? 'border-brand-purple bg-purple-50 shadow-inner' :
                                        wrongMatch?.sId === item.id ? 'border-red-500 bg-red-50' : 'bg-white border-gray-100 hover:border-brand-purple/30'}
                            `}
                        >
                            <div className={`w-16 h-16 ${item.color}`}>
                                <item.Icon fill="currentColor" stroke="currentColor" opacity="0.2" />
                            </div>
                        </motion.button>
                    ))}
                </div>

                <div className="space-y-4">
                    <h4 className="text-center font-bold text-gray-400 uppercase text-xs tracking-widest mb-4">Nombre</h4>
                    {shuffledNames.map((item) => (
                        <motion.button
                            key={item.id}
                            whileHover={{ scale: matches.includes(item.id) ? 1 : 1.05 }}
                            whileTap={{ scale: matches.includes(item.id) ? 1 : 0.95 }}
                            onClick={() => handleNameClick(item.id)}
                            className={`
                                w-full h-24 p-4 rounded-2xl border-4 transition-all flex items-center justify-center
                                font-bold text-xl font-hand tracking-wide
                                ${matches.includes(item.id) ? 'bg-green-50 border-green-500 text-green-700 opacity-50' :
                                    selectedName === item.id ? 'border-brand-purple bg-purple-50 text-brand-purple' :
                                        wrongMatch?.nId === item.id ? 'border-red-500 bg-red-50 text-red-700' : 'bg-white border-gray-100 hover:border-brand-purple/30 text-gray-600'}
                            `}
                        >
                            {item.name}
                        </motion.button>
                    ))}
                </div>
            </div>

            <div className="flex justify-center">
                <button
                    onClick={reset}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-500 rounded-full font-bold hover:bg-gray-200 transition-colors"
                >
                    <RefreshCw size={20} /> Reiniciar Juego
                </button>
            </div>
        </div>
    );
};

const CuerposGeometricos = () => {
    const { saveProgress } = useAuth();

    const quizQuestions = [
        {
            question: "¿Cuál de estos cuerpos geométricos es un CUERPO REDONDO?",
            options: [
                "Un Prisma",
                "Una Pirámide",
                "Una Esfera"
            ],
            correct: 2
        },
        {
            question: "¿Qué característica tienen los cuerpos redondos?",
            options: [
                "Solo tienen caras planas",
                "Tienen al menos una superficie curva",
                "No tienen altura"
            ],
            correct: 1
        },
        {
            question: "Tengo dos bases circulares planas y una superficie curva. ¿Quién soy?",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.Cylinder stroke="#F72585" fill="#F7258520" /></div>,
            options: ["Cono", "Cilindro", "Prisma"],
            correct: 1
        },
        {
            question: "¿Qué piezas forman un Prisma?",
            options: ["Superficies curvas", "Caras, Vértices y Aristas", "Solo dos caras paralelas"],
            correct: 1
        },
        {
            question: "¿Qué cuerpo geométrico NO tiene ni vértices ni aristas rectas?",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.Sphere stroke="#4361EE" fill="#4361EE20" /></div>,
            options: ["Esfera", "Prisma", "Cono"],
            correct: 0
        },
        {
            question: "Las pirámides son poliedros que tienen...",
            visual: <div className="w-24 h-24 mx-auto"><Shapes.Pyramid stroke="#7209B7" fill="#7209B720" /></div>,
            options: [
                "Varias bases",
                "Una sola base y caras laterales que se juntan en la punta",
                "Caras curvas"
            ],
            correct: 1
        }
    ];

    const handleQuizComplete = (score) => {
        saveProgress('unit3', score);
    };

    return (
        <div className="space-y-12 pb-20">
            {/* Header */}
            <header className="text-center space-y-2">
                <span className="bg-brand-green text-white px-4 py-1 rounded-full font-bold text-sm tracking-wide uppercase">
                    Unidad 3
                </span>
                <h1 className="text-4xl font-extrabold text-gray-800 font-hand mt-4">Cuerpos Geométricos</h1>
                <p className="text-xl text-gray-500 mb-4">Descubramos el mundo en 3 Dimensiones.</p>
            </header>

            {/* Theory: What is a solid? */}
            <Section title="¿Qué son los Cuerpos Geométricos?">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-dashed border-green-200 mb-8 text-center flex flex-col md:flex-row items-center gap-6">
                    <div className="flex-1 text-lg text-gray-700 leading-relaxed">
                        Un <span className="font-bold text-brand-green">cuerpo geométrico</span> es una figura que tiene <span className="font-bold text-brand-purple">tres dimensiones</span>: largo, ancho y alto. ¡Ocupan un lugar en el espacio!
                    </div>
                    <div className="flex justify-center gap-6 flex-1">
                        <div className="w-20 h-20 text-brand-blue"><Shapes.CubePrism stroke="currentColor" fill="#4CC9F020" /></div>
                        <div className="w-20 h-20 text-brand-pink"><Shapes.Cylinder stroke="currentColor" fill="#F7258520" /></div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-white p-6 rounded-3xl shadow-lg border-t-8 border-brand-orange flex flex-col gap-4">
                        <h3 className="text-2xl font-bold font-hand text-brand-orange text-center mb-2">Cuerpos Redondos</h3>
                        <p className="text-gray-600 text-center">Tienen superficies curvas. Muchos de ellos ¡pueden rodar!</p>

                        <div className="grid grid-cols-3 gap-2 mt-4">
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-2 text-brand-blue"><Shapes.Sphere stroke="currentColor" fill="#4361EE20" /></div>
                                <span className="font-bold text-sm text-gray-500">Esfera</span>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-2 text-brand-pink"><Shapes.Cylinder stroke="currentColor" fill="#F7258520" /></div>
                                <span className="font-bold text-sm text-gray-500">Cilindro</span>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-2 text-brand-orange"><Shapes.Cone stroke="currentColor" fill="#FF9F1C20" /></div>
                                <span className="font-bold text-sm text-gray-500">Cono</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl shadow-lg border-t-8 border-brand-purple flex flex-col gap-4">
                        <h3 className="text-2xl font-bold font-hand text-brand-purple text-center mb-2">Poliedros</h3>
                        <p className="text-gray-600 text-center">Todas sus caras son planas (como polígonos). Tienen vértices y aristas.</p>

                        <div className="grid grid-cols-2 gap-2 mt-4 justify-center">
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-2 text-brand-green"><Shapes.CubePrism stroke="currentColor" fill="#4CC9F020" /></div>
                                <span className="font-bold text-sm text-gray-500">Prismas / Cubos</span>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-2 text-brand-purple"><Shapes.Pyramid stroke="currentColor" fill="#7209B720" /></div>
                                <span className="font-bold text-sm text-gray-500">Pirámides</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-8">
                    <PrismExplorer />
                    <PyramidExplorer />
                </div>
            </Section>

            {/* Elements of a Polyhedron */}
            <Section title="Partes de un Poliedro">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="bg-slate-50 p-6 rounded-3xl border-2 border-slate-100 relative overflow-hidden flex justify-center py-12">
                        <div className="w-48 h-48 relative">
                            <Shapes.CubePrism stroke="#4CC9F0" fill="#4CC9F010" />

                            {/* Vértice marker (corner) */}
                            <div className="absolute top-[20%] left-[85%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm z-10 animate-pulse" />
                            <div className="absolute top-[13%] left-[90%] text-red-600 font-bold font-hand text-lg z-20 whitespace-nowrap">
                                ← vértice
                            </div>

                            {/* Arista marker (edge) */}
                            <div className="absolute top-[40%] left-[85%] -translate-x-1/2 -translate-y-1/2 w-2 h-16 bg-blue-500 rounded-full border border-white shadow-sm z-10 animate-pulse rotate-0" />
                            <div className="absolute top-[50%] left-[90%] text-blue-600 font-bold font-hand text-lg z-20 whitespace-nowrap">
                                ← arista
                            </div>

                            {/* Cara marker (face) */}
                            <div className="absolute top-[60%] left-[45%] -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-green-500/50 rounded-full border border-green-500 shadow-sm z-10 animate-pulse" />
                            <div className="absolute top-[75%] left-[45%] -translate-x-1/2 text-green-700 font-bold font-hand text-lg z-20 whitespace-nowrap">
                                ↓ cara
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white p-6 rounded-3xl shadow-sm border-2 border-gray-100 flex items-center gap-4">
                            <div className="bg-green-100 p-3 rounded-2xl text-green-600">
                                <Box size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">Caras</h4>
                                <p className="text-sm text-gray-500 text-pretty">Son las superficies planas que forman el poliedro.</p>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-3xl shadow-sm border-2 border-gray-100 flex items-center gap-4">
                            <div className="bg-blue-100 p-3 rounded-2xl text-blue-500">
                                <MousePointer2 size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">Aristas</h4>
                                <p className="text-sm text-gray-500 text-pretty">Son las líneas donde se juntan dos caras.</p>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-3xl shadow-sm border-2 border-gray-100 flex items-center gap-4">
                            <div className="bg-red-100 p-3 rounded-2xl text-red-500">
                                <Info size={24} />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-800">Vértices</h4>
                                <p className="text-sm text-gray-500 text-pretty">Son las esquinas donde se juntan tres o más aristas.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Activity 2.5: Desarrollos Planos */}
            <Section title="Desarrollos Planos" className="border-brand-pink/30">
                <ShapeNets />
            </Section>

            {/* Sub-Activity: Real World Connections */}
            <Section title="¿Dónde los vemos en la vida real?" className="bg-orange-50">
                <p className="text-gray-600 text-center mb-8">¡Los cuerpos geométricos están por todas partes! Fíjate en estos ejemplos:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Cilindro */}
                    <motion.div whileHover={{ y: -5 }} className="bg-white p-6 rounded-2xl shadow-sm border-2 border-brand-pink/20 text-center space-y-4">
                        <div className="w-24 h-24 mx-auto text-brand-pink relative flex items-center justify-center">
                            <Shapes.Cylinder stroke="currentColor" fill="#F7258520" />
                            {/* SVG Cola Can representation */}
                            <svg viewBox="0 0 100 100" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 opacity-30 text-gray-500">
                                <path d="M30 20 Q50 10 70 20 L70 80 Q50 90 30 80 Z" fill="none" stroke="currentColor" strokeWidth="4" />
                                <ellipse cx="50" cy="20" rx="20" ry="10" fill="none" stroke="currentColor" strokeWidth="4" />
                                <line x1="30" y1="40" x2="70" y2="40" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-xl text-brand-pink font-hand">Cilindro</h4>
                        <p className="text-gray-500">Una lata de refresco, un vaso, un rollo de papel...</p>
                    </motion.div>

                    {/* Esfera */}
                    <motion.div whileHover={{ y: -5 }} className="bg-white p-6 rounded-2xl shadow-sm border-2 border-brand-blue/20 text-center space-y-4">
                        <div className="w-24 h-24 mx-auto text-brand-blue relative flex items-center justify-center">
                            <Shapes.Sphere stroke="currentColor" fill="#4361EE20" />
                             {/* SVG Ball representation */}
                             <svg viewBox="0 0 100 100" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 opacity-30 text-gray-500">
                                <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="4" />
                                <path d="M 30 50 Q 50 80 70 50" fill="none" stroke="currentColor" strokeWidth="2" />
                                <path d="M 50 30 Q 80 50 50 70" fill="none" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-xl text-brand-blue font-hand">Esfera</h4>
                        <p className="text-gray-500">Un balón de fútbol, naranjas, la Tierra...</p>
                    </motion.div>

                    {/* Cono */}
                    <motion.div whileHover={{ y: -5 }} className="bg-white p-6 rounded-2xl shadow-sm border-2 border-brand-orange/20 text-center space-y-4">
                        <div className="w-24 h-24 mx-auto text-brand-orange relative flex items-center justify-center">
                            <Shapes.Cone stroke="currentColor" fill="#FF9F1C20" />
                            {/* SVG Ice Cream Cone representation */}
                            <svg viewBox="0 0 100 100" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 opacity-30 text-gray-500">
                                <path d="M25 40 L50 90 L75 40 Z" fill="none" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" />
                                <path d="M 25 40 Q 50 20 75 40" fill="none" stroke="currentColor" strokeWidth="4" />
                                <line x1="35" y1="40" x2="65" y2="70" stroke="currentColor" strokeWidth="2" />
                                <line x1="65" y1="40" x2="35" y2="70" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-xl text-brand-orange font-hand">Cono</h4>
                        <p className="text-gray-500">Un cono de helado, un gorro de fiesta, un cono de tráfico...</p>
                    </motion.div>
                </div>
            </Section>

            {/* Activity 3: Shape Matching Game */}
            <Section title="¡A Jugar! Une el cuerpo con su nombre" className="bg-blue-50/50">
                <p className="text-gray-500 mb-8 italic">Toca una figura 3D y luego su nombre correspondiente.</p>
                <ShapeMatchingGame3D />
            </Section>

            {/* Quiz Section */}
            <div className="py-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-brand-purple mb-2">🧠 ¡Comprueba lo que sabes!</h2>
                    <p className="text-gray-500">Responde a estas preguntas para completar la unidad 3.</p>
                </div>
                <div className="max-w-2xl mx-auto">
                    <Quiz questions={quizQuestions} onComplete={handleQuizComplete} />
                </div>
            </div>
        </div>
    );
};

export default CuerposGeometricos;
